<script>
import { ref, watch } from 'vue';
import pdfMake from 'pdfmake/build/pdfmake';
import { pdfFileName, pdfDateRange, openPdf } from '@/utils/pdfFile';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import CompanyBalanceService from '@/services/CompanyBalanceService';

export default {
  props: {
    company_id: { type: [Number, String], default: null },
    company_name: { type: String, default: '' },
    count_number: { type: Number, default: 0 },
    start_date: { type: [String, Date, Object], default: null },
    end_date: { type: [String, Date, Object], default: null },
  },
  setup(props) {
    const documentDefinition = ref({});

    // 1,234,567.89 — thousands separators, up to 2 decimals.
    const fmt = (value) =>
      Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    // Fixed 2-decimals (opening balance).
    const fmt2 = (value) =>
      Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    // Positive balance = we owe the company (DB); negative = CR.
    const dbcr = (value) => (Number(value) < 0 ? 'CR' : 'DB');
    // Voucher code: purchase (debit) vs payment (credit).
    const voucherCode = (type) => (type === 'debit' ? 'PUR' : type === 'credit' ? 'PAY' : '');

    const normaliseDate = (value) => (value ? moment(value).format('YYYY-MM-DD') : '');

    // Opening balance = running balance just before the first in-range row.
    // Each row's remaining_amount is the balance AFTER it, so:
    //   opening = firstRemaining - firstDebit + firstCredit
    const computeOpening = (transactions) => {
      const first = transactions.find(
        (t) => t.remaining_amount !== null && t.remaining_amount !== undefined
      );
      if (!first) return 0;
      const amt = Number(first.amount || 0);
      const debit = first.type === 'debit' ? amt : 0;
      const credit = first.type === 'credit' ? amt : 0;
      return Number(first.remaining_amount) - debit + credit;
    };

    const buildCompanySection = (company, rangeLabel) => {
      const transactions = Array.isArray(company.transactions) ? company.transactions : [];
      const opening = computeOpening(transactions);

      const body = [
        [
          { text: 'Voucher#', style: 'th' },
          { text: 'Date', style: 'th', alignment: 'center' },
          { text: 'Description', style: 'th' },
          { text: 'Debit', style: 'th', alignment: 'right' },
          { text: 'Credit', style: 'th', alignment: 'right' },
          { text: 'Total', style: 'th', alignment: 'right' },
        ],
      ];

      let running = opening;
      let totalDebit = 0;
      let totalCredit = 0;

      transactions.forEach((row) => {
        const amt = Number(row.amount || 0);
        const debit = row.type === 'debit' ? amt : 0;
        const credit = row.type === 'credit' ? amt : 0;
        running = running + debit - credit;
        totalDebit += debit;
        totalCredit += credit;

        // Purchases show the bill's invoice number when one was entered.
        const desc =
          row.description ||
          (row.type === 'debit'
            ? (row.invoice_number ? `Invoice # ${row.invoice_number}` : 'Purchase')
            : row.type === 'credit' ? 'Amount Paid' : '');
        // Payments made via a shop/salesman get a shop header row above them,
        // styled like the salesman ledger (grey shop id, bold name - address).
        if (row.shop_name) {
          body.push([
            { text: '' },
            {
              text: row.shop_id != null ? `${row.shop_id}` : '',
              style: 'groupHeader',
              alignment: 'center',
              bold: true,
              fontSize: 11,
              color: '#777',
            },
            {
              colSpan: 4,
              style: 'groupHeader',
              text: [
                { text: row.shop_name, bold: true, fontSize: 13, color: '#000' },
                {
                  text: row.shop_address ? `  -  ${row.shop_address}` : '',
                  bold: true,
                  fontSize: 11,
                  color: '#000',
                },
              ],
            },
            {}, {}, {},
          ]);
        }
        const descCell = {
          text: row.salesman_name ? `${desc}   (${row.salesman_name})` : desc,
          style: 'cell',
        };

        body.push([
          { text: `${voucherCode(row.type)} ${row.id}`, style: 'cell' },
          {
            text: row.payment_at ? moment(row.payment_at).format('DD-MM-YY') : '',
            style: 'cell',
            alignment: 'center',
          },
          descCell,
          { text: debit ? fmt(debit) : '-', style: 'cell', alignment: 'right' },
          { text: credit ? fmt(credit) : '-', style: 'cell', alignment: 'right' },
          { text: `${fmt(running)} ${dbcr(running)}`, style: 'cell', alignment: 'right' },
        ]);
      });

      const section = [
        {
          columns: [
            {
              width: '*',
              text: [
                { text: 'Account : ', style: 'hdrLabel' },
                { text: company.company_name || '', style: 'hdrTitle' },
                { text: `     Code : ${company.company_id ?? ''}`, style: 'hdrLabel' },
              ],
            },
            { width: 'auto', alignment: 'right', text: rangeLabel, style: 'hdrLabel' },
          ],
          margin: [0, 0, 0, 2],
        },
        {
          alignment: 'right',
          text: [
            { text: 'Opening Balance : ', style: 'hdrLabel' },
            { text: `${fmt2(opening)} ${dbcr(opening)}`, style: 'hdrTitle' },
          ],
          margin: [0, 0, 0, 4],
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [50, 44, '*', 65, 65, 85],
            body,
          },
          layout: {
            hLineWidth: (i, node) => (i === 1 || i === node.table.body.length ? 0.6 : 0.3),
            hLineColor: () => '#CCC',
            vLineWidth: () => 0,
            paddingTop: () => 3,
            paddingBottom: () => 3,
          },
        },
        {
          margin: [0, 2, 0, 0],
          table: {
            widths: ['*', 65, 65, 85],
            body: [
              [
                { text: 'End of Report Total >>>', alignment: 'right', style: 'totalCell', border: [false, false, false, false] },
                { text: fmt(totalDebit), style: 'totalCell', alignment: 'right', border: [true, true, true, true] },
                { text: fmt(totalCredit), style: 'totalCell', alignment: 'right', border: [true, true, true, true] },
                { text: `${fmt(running)} ${dbcr(running)}`, style: 'totalCell', alignment: 'right', border: [true, true, true, true] },
              ],
            ],
          },
          layout: {
            hLineColor: () => '#333',
            vLineColor: () => '#333',
            paddingLeft: () => 3,
            paddingRight: () => 3,
            paddingTop: () => 3,
            paddingBottom: () => 3,
          },
        },
      ];

      if (!transactions.length) {
        section.splice(2, 1, {
          text: 'No records found for the selected range.',
          style: 'cell',
          margin: [0, 6, 0, 0],
        });
        section.pop(); // drop the totals box when there are no rows
      }

      return section;
    };

    const loadPdf = async () => {
      const start = normaliseDate(props.start_date);
      const end = normaliseDate(props.end_date);
      const rangeLabel =
        start && end
          ? `${moment(props.start_date).format('DD-MMM-YYYY')}  to  ${moment(props.end_date).format('DD-MMM-YYYY')}`
          : 'All Dates';

      const selectedCompanyId = Number(props.company_id ?? 0);

      let dataset = [];
      try {
        const response = await CompanyBalanceService.getTransactionsPdf({
          companyId: selectedCompanyId > 0 ? selectedCompanyId : undefined,
          startDate: start,
          endDate: end,
        });
        dataset = Array.isArray(response?.data?.companies) ? response.data.companies : [];
      } catch (e) {
        dataset = [];
      }

      if (selectedCompanyId > 0 && !dataset.length) {
        dataset = [{
          company_id: selectedCompanyId,
          company_name: props.company_name || `Company #${selectedCompanyId}`,
          transactions: [],
        }];
      }
      if (selectedCompanyId > 0) {
        dataset = dataset.map((c) => ({
          company_id: c.company_id ?? selectedCompanyId,
          company_name: c.company_name || props.company_name || `Company #${selectedCompanyId}`,
          transactions: c.transactions || [],
        }));
      }

      const content = [];
      content.push({
        columns: [
          { width: '*', text: 'Company Ledger', style: 'reportTitle' },
          { width: 'auto', alignment: 'right', text: `Print Date : ${moment().format('DD-MMM-YYYY')}`, style: 'hdrSmall' },
        ],
        margin: [0, 0, 0, 8],
      });

      dataset.forEach((company, index) => {
        buildCompanySection(company, rangeLabel).forEach((block) => content.push(block));
        if (index !== dataset.length - 1) {
          content.push({ text: '', margin: [0, 0, 0, 14] });
        }
      });

      documentDefinition.value = {
        pageSize: 'A4',
        pageMargins: [40, 40, 40, 30],
        content,
        styles: {
          reportTitle: { fontSize: 14, bold: true },
          th: { bold: true, fontSize: 10, fillColor: '#EDEDED', margin: [0, 2, 0, 2] },
          cell: { fontSize: 10, margin: [0, 1, 0, 1] },
          groupHeader: { fontSize: 10, margin: [0, 3, 0, 1] },
          totalCell: { bold: true, fontSize: 9, margin: [0, 3, 0, 3] },
          hdrSmall: { fontSize: 8 },
          hdrLabel: { fontSize: 9 },
          hdrTitle: { fontSize: 11, bold: true },
        },
      };

      pdfMake.vfs = pdfFonts;
      openPdf(pdfMake, documentDefinition.value, pdfFileName(['Company-Ledger', props.company_name || 'All-Companies'], pdfDateRange(props.start_date, props.end_date)));
    };

    watch(
      () => props.count_number,
      (currentValue, oldValue) => {
        if (currentValue !== oldValue && currentValue) {
          loadPdf();
        }
      }
    );

    return {};
  },
};
</script>
